import { useEffect, useState, createContext, useContext } from "react";
import { api, auth } from "@/lib/api";

const BrandingCtx = createContext({ branding: null, refresh: () => {}, user: null });

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(null);
  const [user, setUser] = useState(null);

  async function refresh() {
    try {
      const r = await api.get("/branding");
      setBranding(r.data);
      // apply theme color
      const c = r.data?.theme_color || "#0EA5E9";
      applyTheme(c);
      // set hospital name in <title>
      if (r.data?.hospital_name) {
        document.title = `${r.data.hospital_name} · Training`;
      }
    } catch (e) {/* no-op */}
  }

  async function loadUser() {
    if (!auth.token()) { setUser(null); return; }
    try { const r = await api.get("/auth/me"); setUser(r.data); }
    catch { setUser(null); }
  }

  useEffect(() => { refresh(); loadUser(); }, []);

  return <BrandingCtx.Provider value={{ branding, refresh, user, reloadUser: loadUser }}>{children}</BrandingCtx.Provider>;
}

export function useBranding() { return useContext(BrandingCtx); }

function applyTheme(hex) {
  // Convert hex to HSL components so Tailwind/Shadcn CSS vars (hsl(var(--primary))) pick it up
  const { h, s, l } = hexToHsl(hex);
  const root = document.documentElement;
  root.style.setProperty("--primary", `${h} ${s}% ${l}%`);
  root.style.setProperty("--ring", `${h} ${s}% ${l}%`);
  // also set a CSS var hospitals brand for inline use
  root.style.setProperty("--brand-color", hex);
  // sky-500 style replacements: simply write a meta theme-color (PWA)
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) { meta = document.createElement("meta"); meta.name = "theme-color"; document.head.appendChild(meta); }
  meta.content = hex;
}

function hexToHsl(hex) {
  hex = hex.replace("#", "");
  if (hex.length === 3) hex = hex.split("").map(c => c + c).join("");
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}
